/**
 * Display-only catalog for pricing cards, keyed by Stripe product ID.
 *
 * Prices, intervals, seats, and credits are fetched live from the metering
 * service (`GET /api/billing/pricing-plans`). This module only holds the
 * marketing copy (tagline, bullets) and the "recommended" highlight flag
 * so the Stripe dashboard stays the source of truth for pricing and quota.
 *
 * @module lib/pricingPlans
 */
import { resolvePlanTier } from './planConfig';
type PlanTier = 'free' | 'pro' | 'business';


/** Plan record as returned by the metering service. */
export interface ApiPricingPlan {
  product_id: string;
  price_id: string;
  name: string;
  description: string | null;
  unit_amount: number;
  currency: string;
  interval: 'month' | 'year';
  seats: number;
  credits_per_month: number;
}

/** Hardcoded display copy per Stripe product. */
export interface PlanDisplay {
  /** Short pitch shown under the name. */
  tagline: string;
  /** Name of the tier this one builds on (shown as "Everything in X, plus:"). Null for the base tier. */
  inheritsFrom: string | null;
  /** Scan-ready feature lines beyond what's inherited (seats/credits are rendered separately). */
  bullets: string[];
  /** Whether this product should be visually highlighted as the recommended tier. */
  highlighted?: boolean;
}

const PLAN_DISPLAY_BY_TIER: Record<PlanTier, PlanDisplay> = {
  free: {
    tagline: 'Keep the conversation going, for free.',
    inheritsFrom: null,
    bullets: ['Text conversations', 'AI builder and editing', 'Website embeds', 'Multiple languages', 'Basic analytics'],
  },
  pro: {
    tagline: 'Add calls, your brand and automation.',
    inheritsFrom: 'Free',
    bullets: ['Audio and video calls', 'Your logo and colours', 'Completion redirects', 'API and MCP access'],
    highlighted: true,
  },
  business: {
    tagline: 'Bring your team. Go deeper together.',
    inheritsFrom: 'Pro',
    bullets: ['Screen sharing', 'Sentiment and theme analytics', 'Remove holito attribution', 'Priority support'],
  },
};

/**
 * Resolve display copy from product_id for the current environment.
 * Unknown products follow the canonical resolver's fail-closed Free fallback
 * and telemetry, rather than interrupting rendering.
 */
export function resolvePlanDisplay(productId: string): PlanDisplay {
  const tier = resolvePlanTier(productId);
  return PLAN_DISPLAY_BY_TIER[tier];
}

/** Currency-code → symbol lookup for display. */
const CURRENCY_SYMBOLS: Record<string, string> = {
  gbp: '£',
  usd: '$',
  eur: '€',
};

/**
 * Formats a plan's unit amount (in Stripe minor units) for display.
 * Returns "Free" for zero-amount plans.
 */
export function formatAmount(unitAmount: number, currency: string): string {
  if (unitAmount === 0) return 'Free';
  const symbol = CURRENCY_SYMBOLS[currency.toLowerCase()] ?? '';
  return `${symbol}${Math.round(unitAmount / 100)}`;
}

/**
 * Calculates the monthly-equivalent amount of a yearly price so
 * monthly/yearly totals can be compared at a glance.
 */
export function monthlyEquivalent(yearlyUnitAmount: number): number {
  return Math.round(yearlyUnitAmount / 12);
}

/**
 * Returns the percentage saved by picking the yearly plan over the monthly plan.
 * Returns 0 when either side is missing or non-positive.
 */
export function yearlySavingsPercent(monthlyUnitAmount: number, yearlyUnitAmount: number): number {
  if (monthlyUnitAmount <= 0 || yearlyUnitAmount <= 0) return 0;
  const fullYearAtMonthly = monthlyUnitAmount * 12;
  if (fullYearAtMonthly <= yearlyUnitAmount) return 0;
  return Math.round(((fullYearAtMonthly - yearlyUnitAmount) / fullYearAtMonthly) * 100);
}
